import { isNil } from "lodash";

import {
  initObservability,
  InternalLoggerLevel,
  LogLevel,
  pushError,
  pushLog,
  setUser as setO11yUser,
} from "@8x8/pui-observability";

import { getEnv } from "@/env";
import { getBEAppVersion } from "@/react/services/common/metadataApi";
import Debug from "@/react/debug";

const DEFAULT_INTERNAL_LOGGING_LEVEL = InternalLoggerLevel.INFO;

const selectInternalLogLevel = () => {
  let level = DEFAULT_INTERNAL_LOGGING_LEVEL;
  try {
    level = o11yParseInternalLogLevel(
      getEnv("VUE_APP_O11Y_INTERNAL_LOG_LEVEL")
    );
  } catch (e) {
    Debug.error(
      `Failed to parse internal log level, using default ${level}`,
      e
    );
  }
  return level;
};

const compileVersionString = async () => {
  let version = `fe-v=${getEnv("VUE_APP_VERSION")}`;
  try {
    const beVersion = await getBEAppVersion();
    version = `${version};be-v=${beVersion}`;
  } catch (e) {
    console.error("Failed to get BE app version", e);
  }
  Debug.log("FE/BE Version", version);
  return version;
};

export const initO11y = async () => {
  Debug.log(
    "Init Observability with ",
    getEnv("VUE_APP_O11Y_COLLECT_URL"),
    getEnv("VUE_APP_O11Y_INTERNAL_LOG_LEVEL")
  );

  initObservability({
    collectorUrl: getEnv("VUE_APP_O11Y_COLLECT_URL"),
    apiKey: getEnv("VUE_APP_O11Y_API_KEY"),
    app: {
      name: "tap-frontend",
      namespace: "tap",
      release: getEnv("VUE_APP_VERSION"),
      version: await compileVersionString(),
      environment: getEnv("VUE_APP_NODE_ENV"),
    },
    internalLoggerLevel: selectInternalLogLevel(),
  });
};

export const updateO11yUserData = (userData) => {
  Debug.log("Updating o11y data with", userData);
  const user = userData.user;
  const attributes = userData.attributes;

  // We can only send string values for the attributes, so flatten anything that isn't a string
  const alteredAttributes = {};
  for (const key in attributes) {
    const value = attributes[key];
    if (!isNil(value)) {
      // If the value is not nil and not a string, then we convert to JSON
      if (typeof value !== "string" && !(value instanceof String)) {
        Debug.warn(
          `Value of attribute ${key} is not a string; converting to JSON`
        );
        alteredAttributes[key] = JSON.stringify(value);
      } else {
        alteredAttributes[key] = value;
      }
    }
  }

  /* TODO
    Due to OX-2896, we reset the entire user object whenever we update attributes
    In the future, we should just use `setUserAttributes`, when it updates correctly
   */
  // Call the o11y API
  setO11yUser({
    id: user?.email,
    attributes: alteredAttributes,
  });
  Debug.log("Updated o11y data with", user, alteredAttributes);
};

const o11yParseInternalLogLevel = (level) => {
  if (typeof level !== "string" && !(level instanceof String)) {
    throw Error(`Invalid object given for level; must be a string: ${level}`);
  }
  switch (level.toUpperCase()) {
    case "ERROR":
      return InternalLoggerLevel.ERROR;
    case "WARN":
      return InternalLoggerLevel.WARN;
    case "INFO":
      return InternalLoggerLevel.INFO;
    case "DEBUG":
    case "VERBOSE":
      return InternalLoggerLevel.VERBOSE;
    case "OFF":
      return InternalLoggerLevel.OFF;
    default:
      throw Error(`Invalid internal logger level ${level}`);
  }
};

const shouldSkipDedupe = () => {
  return !getEnv("VUE_APP_O11Y_DEDUPLICATE_LOGS", true);
};

export const sendLog = (message, level, context) => {
  pushLog([message], {
    context: context,
    level: level,
    skipDedupe: shouldSkipDedupe(),
  });
};

export const sendError = (error, context = undefined, logToConsole = true) => {
  if (logToConsole) {
    console.error(error);
  }
  if (error instanceof Error) {
    pushError(error, {
      context: context,
      skipDedupe: shouldSkipDedupe(),
      type: error.name ?? context.type,
    });
  } else if (typeof error === "string" || error instanceof String) {
    sendLog(error, LogLevel.ERROR, context);
  } else {
    throw Error(
      "Sending errors for observability should use either string messages or 'Error' objects"
    );
  }
};

export const sendWarn = (message, context = undefined, logToConsole = true) => {
  if (logToConsole) {
    console.warn(message);
  }
  sendLog(message, LogLevel.WARN, context);
};

export const sendInfo = (message, context = undefined, logToConsole = true) => {
  if (logToConsole) {
    console.info(message);
  }
  sendLog(message, LogLevel.INFO, context);
};

export const sendDebug = (
  message,
  context = undefined,
  logToConsole = true
) => {
  if (logToConsole) {
    console.debug(message);
  }
  sendLog(message, LogLevel.DEBUG, context);
};

export const sendTrace = (
  message,
  context = undefined,
  logToConsole = true
) => {
  if (logToConsole) {
    console.trace(message);
  }
  sendLog(message, LogLevel.TRACE, context);
};
